// packages/core/src/diff/interfaces.ts
import type { ResolvedInterface } from "../resolver.js";
import type { InterfaceDiff, Status } from "../diff-types.js";
import { diffFields } from "./fields-aspects.js";
import { scoreRename, RENAME_THRESHOLD } from "./rename-heuristic.js";

export function diffInterfaces(
  before: readonly ResolvedInterface[],
  after: readonly ResolvedInterface[]
): Map<string, InterfaceDiff> {
  const byNameBefore = new Map(before.map((i) => [i.name, i]));
  const byNameAfter = new Map(after.map((i) => [i.name, i]));

  const out = new Map<string, InterfaceDiff>();
  const matched = new Set<string>();

  // Pass 1: exact-name matches.
  for (const [name, b] of byNameBefore) {
    const a = byNameAfter.get(name);
    if (!a) continue;
    out.set(name, matched1(b, a));
    matched.add(`B:${name}`);
    matched.add(`A:${name}`);
  }

  // Pass 2: rename heuristic over remaining.
  const remainingBefore = [...byNameBefore.values()].filter(
    (i) => !matched.has(`B:${i.name}`)
  );
  const remainingAfter = [...byNameAfter.values()].filter(
    (i) => !matched.has(`A:${i.name}`)
  );

  for (const b of remainingBefore) {
    let best: { a: ResolvedInterface; score: number } | null = null;
    for (const a of remainingAfter) {
      if (matched.has(`A:${a.name}`)) continue;
      const score = scoreRename(toCandidate(b), toCandidate(a));
      if (!best || score > best.score) best = { a, score };
    }
    if (best && best.score >= RENAME_THRESHOLD) {
      const fields = diffFields(b.body.fields, best.a.body.fields);
      out.set(best.a.name, {
        name: best.a.name,
        status: "renamed",
        pathChange: { from: b.name, to: best.a.name },
        typeChange:
          b.typeName !== best.a.typeName
            ? { from: b.typeName, to: best.a.typeName }
            : undefined,
        fields,
        sourceFile: best.a.decl.source?.file,
      });
      matched.add(`B:${b.name}`);
      matched.add(`A:${best.a.name}`);
    }
  }

  // Pass 3: unmatched leftovers become add/remove.
  for (const b of remainingBefore) {
    if (matched.has(`B:${b.name}`)) continue;
    out.set(`__removed__${b.name}`, {
      name: b.name,
      status: "removed",
      fields: new Map(),
      sourceFile: b.decl.source?.file,
    });
  }
  for (const a of remainingAfter) {
    if (matched.has(`A:${a.name}`)) continue;
    out.set(a.name, {
      name: a.name,
      status: "added",
      fields: new Map(),
      sourceFile: a.decl.source?.file,
    });
  }
  return out;
}

function matched1(b: ResolvedInterface, a: ResolvedInterface): InterfaceDiff {
  const fields = diffFields(b.body.fields, a.body.fields);
  const typeChanged = b.typeName !== a.typeName;
  const anyInner = fields.size > 0;
  let status: Status = "unchanged";
  if (typeChanged || anyInner) status = "modified";
  return {
    name: a.name,
    status,
    typeChange: typeChanged ? { from: b.typeName, to: a.typeName } : undefined,
    fields,
    sourceFile: a.decl.source?.file,
  };
}

function toCandidate(i: ResolvedInterface) {
  return {
    kind: i.typeName,
    name: i.name,
    fieldKeys: i.body.fields.map((f) => f.path.join(".")),
  };
}
